import { useState, useEffect, useCallback } from "react";
import {
  getRevenueBootstrapData,
  getRevenueBootstrapError,
  getRevenueBootstrapPromise,
} from "../state/revenueBootstrapStore";
import { useRevenueBootstrap } from "./useRevenueBootstrap";

export function useTwelveMonthRevenueTrend() {
  useRevenueBootstrap();

  const [labels, setLabels] = useState<string[]>([]);
  const [values, setValues] = useState<number[]>([]);
  const [barVariants, setBarVariants] = useState<("normal" | "strong")[]>([]);
  const [averageRevenue, setAverageRevenue] = useState<number>(0);
  const [rangeLabel, setRangeLabel] = useState<string>("");
  const [monthCount, setMonthCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const syncFromBootstrap = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      let bootstrapEntity = getRevenueBootstrapData();
      if (!bootstrapEntity) {
        const bootstrapPromise = getRevenueBootstrapPromise();
        if (bootstrapPromise) {
          bootstrapEntity = await bootstrapPromise;
        }
      }

      const bootstrapError = getRevenueBootstrapError();
      if (!bootstrapEntity) {
        throw new Error(bootstrapError ?? "Revenue bootstrap data unavailable");
      }

      const section = bootstrapEntity.sections.twelveMonthTrend;
      if (section.status !== "ok" || !section.data) {
        throw new Error(section.error ?? "12-month revenue trend unavailable");
      }

      const entity = section.data;
      setLabels(entity.labels);
      setValues(entity.values);
      setBarVariants(entity.variants);
      setAverageRevenue(entity.averageRevenue);
      setRangeLabel(entity.rangeLabel);
      setMonthCount(entity.monthCount);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load 12-month revenue trend";
      setError(message);
      setLabels([]);
      setValues([]);
      setBarVariants([]);
      setAverageRevenue(0);
      setRangeLabel("");
      setMonthCount(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void syncFromBootstrap();
  }, [syncFromBootstrap]);

  return {
    labels,
    values,
    barVariants,
    averageRevenue,
    rangeLabel,
    monthCount,
    loading,
    error,
    refetch: syncFromBootstrap,
  };
}
